import { Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AuthHandler } from '@usimweb/core-auth';
import { ApiService } from '@usimweb/core/services';
import { Token, Comments, Env } from '@usimweb/core/models';
import { Helper } from '../helper';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';

import * as resultActions from '../actions/result.action';

@Injectable()
export class ResultEffects {
  constructor(
    @Inject('environment') private environment: Env,
    private actions$: Actions,
    private apiService: ApiService,
    private authHandler: AuthHandler,
    private snackBar: MatSnackBar
  ) {}

  private DEBUG_TIME: number = this.environment.production === true ? 0 : 1000;

  $loadResults = createEffect(() => {
    return this.actions$.pipe(
      ofType(resultActions.LOAD_RESULT),
      map((action: resultActions.LoadResult) => action.payload),
      switchMap((obj) => {
        return this.apiService.getFoamingResult(obj.uuid, obj.filling).pipe(
          map((result) => {
            return new resultActions.LoadResultSuccess(result);
          }),
          catchError((error) => of(new resultActions.LoadResultFailed(error)))
        );
      })
    );
  });

  $getCeetronToken = createEffect(() => {
    return this.actions$.pipe(
      ofType(resultActions.LOAD_TOKEN),
      map((action: resultActions.LoadToken) => action.payload),
      switchMap((uuid: string) => {
        const access_token = this.authHandler.accessToken || '';
        return this.apiService.getCeetronToken(uuid, access_token).pipe(
          map((token) => {
            const tokenDict = {
              access_token: this.authHandler.accessToken,
              modelKey: token.modelKey,
            } as Token;
            return new resultActions.LoadTokenSuccess(tokenDict);
          }),
          catchError((error) => of(new resultActions.LoadTokenFailed(error)))
        );
      })
    );
  });

  $loadResultCurves = createEffect(() => {
    return this.actions$.pipe(
      ofType(resultActions.LOAD_CURVES),
      map((action: resultActions.LoadCurves) => action.payload),
      switchMap((obj) => {
        return this.apiService.getResultCurves(obj.uuid, obj.filling).pipe(
          map((result: any) => {
            return new resultActions.LoadCurvesSuccess(result);
          }),
          catchError((error) => of(new resultActions.LoadCurvesFailed(error)))
        );
      })
    );
  });

  $addComment = createEffect(() => {
    return this.actions$.pipe(
      ofType(resultActions.ADD_COMMENTS),
      map((action: resultActions.AddComments) => action.payload),
      switchMap((obj) => {
        return this.apiService.addProjectComment(obj.comment, obj.version_uuid).pipe(
          map(() => {
            // FIXME backend needs to return comment
            const fakeComment = [
              {
                uuid: obj.version_uuid,
                // created_at: new Date().toDateString(),
                created_by: AuthHandler.userName || '',
                content: obj.comment.content,
                formatted_date: new Date().toDateString(),
                // timestamp: Date.now(),
                // tags: ['v_' + obj.project_uuid],
              },
            ] as Comments[];

            return new resultActions.AddCommentsSuccess(fakeComment);
          }),
          catchError((error) => of(new resultActions.AddCommentsFailed(error)))
        );
      })
    );
  });

  $loadProjectsComments = createEffect(() => {
    return this.actions$.pipe(
      ofType(resultActions.LOAD_PROJECTS_COMMENTS),
      map((action: resultActions.LoadProjectsComments) => action.payload),
      switchMap((uuid) => {
        return this.apiService.getProjectComments(uuid).pipe(
          map((result) => {
            this.snackBar.open('Loaded project chat...', 'OK', { duration: 2000 });
            return new resultActions.LoadProjectsCommentsSuccess(result);
          }),
          catchError((error) => of(new resultActions.LoadProjectsCommentsFailed(error)))
        );
      })
    );
  });

  $loadVersionComments = createEffect(() => {
    return this.actions$.pipe(
      ofType(resultActions.LOAD_VERSION_COMMENTS),
      map((action: resultActions.LoadVersionComments) => action.payload),
      switchMap((uuid) => {
        return this.apiService.getVersionComments(uuid).pipe(
          map((result) => {
            this.snackBar.open('Loaded version chat...', 'OK', { duration: 2000 });
            return new resultActions.LoadVersionsCommentsSuccess(result);
          }),
          catchError((error) => of(new resultActions.LoadVersionsCommentsFailed(error)))
        );
      })
    );
  });

  $deleteProjectComments = createEffect(() => {
    return this.actions$.pipe(
      ofType(resultActions.RM_COMMENTS),
      map((action: resultActions.RmComments) => action.payload),
      switchMap((obj) => {
        return this.apiService.deleteProjectComment(obj.uuid, obj.comment_uuid).pipe(
          map((result) => {
            this.snackBar.open('Deleted comment...', 'OK', { duration: 2000 });
            return new resultActions.RmCommentsSuccess(result);
          }),
          catchError((error) => of(new resultActions.RmCommentsFailed(error)))
        );
      })
    );
  });

  //   $measurements = createEffect(() => {});

  $getFoamingPpx = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(resultActions.LOAD_PPX),
        map((action: resultActions.LoadPpx) => action.payload),
        switchMap((obj) => {
          return this.apiService.getFoamingPpx(obj.uuid).pipe(
            map((result) => {
              if (result) {
                const blob = new Blob([result], {});
                Helper.createDownLoadFile(blob, '.pptx', 'presentation_' + obj.name);
                this.snackBar.open('Download finished...', 'OK', { duration: 2000 });
                return new resultActions.LoadPpxSuccess(result);
              }
              this.snackBar.open('No presentation available...', 'OK', { duration: 5000 });
              return new resultActions.LoadPpxSuccess(result);
            }),
            catchError((error) => of(new resultActions.LoadPpxFailed(error)))
          );
        })
      );
    },
    { dispatch: false }
  );

  $retryVersion = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(resultActions.RETRY_VERSION),
        map((action: resultActions.RetryVersion) => action.payload),
        switchMap((uuid) => {
          return this.apiService.retryVersion(uuid).pipe(
            map((result) => {
              this.snackBar.open('Restarted simulation ...', 'OK', {
                duration: 2000,
              });
              return new resultActions.RetryVersionSuccess(result);
            }),
            catchError((error) => of(new resultActions.RetryVersionFailed(error)))
          );
        })
      );
    },
    { dispatch: false }
  );
}
